import React from 'react';
import { Check } from 'lucide-react';
import API from '@/lib/API';
import { showApiError } from '@/lib/toast';

export const LEAD_EXECUTION_STAGES = [
  'Design',
  'Budget',
  'Ordering',
  'Sign Off copy',
  'Payments',
  'Execution Not Started',
  'Team Assignment',
  // 'Material Requested',  // Not required
  // 'Pending Store',       // Not required
  // 'Owner Approval',      // Not required
  // 'Dispatched',          // Not required
  // 'Project Started',     // Not required
  'Installation',
  'Documents',
  // 'Verify Payments',     // Not required
  'Completed'
];

// Legacy backend status values → pipeline index mapping
export const LEGACY_STATUS_MAP = {
  'new': 0,
  'converted': 0,
  'advance_payment_10': 4,  // Payments stage
  'budget': 1,
  'sign_of_copy': 3,
  'advance_payment_50': 4,
  'prod_drawings': 0,
  'final_payment': 4,
  'delivery': 4,
  'not started': 0,
  'execution not started': 5,
  'order placing': 2,
  // Commented-out stages still need to map for existing projects
  'material requested': 6,  // → Team Assignment index (6)
  'pending store': 6,
  'owner approval': 6,
  'dispatched': 6,
  'project started': 7,     // → Installation index (7)
};

export const LeadExecutionPipeline = ({ project, onUpdate }) => {
  const currentStatus = project?.status || 'Not Started';
  const projectId = project?.projectId || project?._id;



  // Find index by exact stage name first, then try legacy map
  let activeIndex = LEAD_EXECUTION_STAGES.findIndex(s => s.toLowerCase() === currentStatus.toLowerCase());
  if (activeIndex === -1) {
    const legacyIdx = LEGACY_STATUS_MAP[currentStatus.toLowerCase()];
    activeIndex = legacyIdx !== undefined ? legacyIdx : 0;
  }

  // Prevent pipeline from visually regressing once a stage is completed
  const [displayIndex, setDisplayIndex] = React.useState(activeIndex);

  React.useEffect(() => {
    if (projectId) {
      const cacheKey = `verve_pipeline_max_${projectId}`;
      const cached = localStorage.getItem(cacheKey);
      const cachedMax = cached ? parseInt(cached, 10) : 0;
      
      const newMax = Math.max(activeIndex, cachedMax);
      if (newMax > cachedMax) {
        localStorage.setItem(cacheKey, newMax.toString());
      }
      setDisplayIndex(newMax);
    } else {
      setDisplayIndex(activeIndex);
    }
  }, [projectId, activeIndex]);


  return (
    <div className="bg-white rounded-xl border border-slate-100 py-5 overflow-x-auto no-scrollbar w-full mb-3">
      <div className="flex items-start justify-between min-w-[1600px] px-8">
        {LEAD_EXECUTION_STAGES.map((stage, index) => {
          const isLastStep = index === LEAD_EXECUTION_STAGES.length - 1;
          const isCompleted = index < displayIndex || (isLastStep && currentStatus.toLowerCase() === 'completed');
          const isActive = index === displayIndex && !isCompleted;

          return (
            <div key={stage} className="flex flex-col items-center gap-2 flex-1 relative group">
              {index !== LEAD_EXECUTION_STAGES.length - 1 && (
                <div
                  className={`absolute top-4 left-[50%] w-full h-[2px] transition-colors duration-500 ${
                    isCompleted ? 'bg-emerald-400' : 'bg-slate-100'
                  }`}
                />
              )}

              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-extrabold z-10 transition-all duration-300 border-2 ${
                  isCompleted
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : isActive
                      ? 'bg-[#2b448f] border-[#2b448f] text-white'
                      : 'bg-white border-slate-200 text-slate-300'
                }`}
              >
                {isCompleted ? <Check className="w-4 h-4 stroke-[3]" /> : index + 1}
              </div>

              <div className="flex flex-col items-center text-center max-w-[100px]">
                <span
                  className={`text-[9px] font-extrabold uppercase leading-tight tracking-tighter ${
                    isActive ? 'text-[#2b448f]' : isCompleted ? 'text-emerald-600' : 'text-slate-400'
                  }`}
                >
                  {stage === 'Execution Not Started' ? 'Not Started' : stage}
                </span>
                {isActive && <div className="w-1 h-1 rounded-full bg-[#2b448f] mt-1 animate-pulse" />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
